import { useState } from "react";
import { JsonBlock } from "@/components/ui/json-block";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  RefreshCw,
  Loader2,
  FileJson,
  FileText,
  Play,
  Globe,
  Database,
  BarChart3,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RotateCcw,
} from "lucide-react";
import { useJobList, useDeleteJobs } from "@/hooks/useJobs";
import { useUrlsSummary } from "@/hooks/useValidation";
import {
  useScraperRunLogs,
  useScraperRuns,
  useScrapersSources,
} from "@/hooks/useScrapersSources";
import { useUrlState } from "@/hooks/useUrlState";
import { useSessionState } from "@/hooks/useSessionState";
import { Toaster, toast } from "sonner";
import { cn } from "@/lib/utils";
import type { JobListItem } from "@/services/jobsApi";
import type { ScraperLog, ScraperRun } from "@/services/scrapersApi";
import type { ScrapesUrlState, ScrapesSessionState } from "@/types/pageState";
import { scrapeUrl } from "@/services/validationApi";
import { JobResultModal } from "@/components/jobs/JobResultModal";
import { UrlScrapeDialog } from "@/components/validation/UrlScrapeDialog";
import { SourceScrapeDialog } from "@/components/validation/SourceScrapeDialog";

const SCRAPE_FUNCTION_PATTERN = /scrape/i;

type SortKey = "job_id" | "function" | "status" | "enqueue_time";
type SortDirection = "asc" | "desc";

const DEFAULT_SORT_KEY: SortKey = "enqueue_time";
const DEFAULT_SORT_DIRECTION: SortDirection = "desc";

function renderSortIcon(
  columnKey: SortKey,
  sortKey: SortKey,
  sortDirection: SortDirection,
) {
  const isActive = sortKey === columnKey;
  if (!isActive) {
    return <ArrowUpDown className="w-3 h-3 text-muted-foreground" />;
  }
  return sortDirection === "asc" ? (
    <ArrowUp className="w-3 h-3" />
  ) : (
    <ArrowDown className="w-3 h-3" />
  );
}

function StatusBadge({ status }: { status: string }) {
  const variant =
    status === "in_progress" || status === "running"
      ? "default"
      : status === "queued"
        ? "secondary"
        : status === "complete" || status === "completed"
          ? "outline"
          : status === "failed"
            ? "destructive"
            : "outline";
  const label =
    status === "in_progress"
      ? "In progress"
      : status === "running"
        ? "Running"
        : status === "completed"
          ? "Completed"
          : status === "not_found"
            ? "Not found"
            : status;
  return (
    <Badge variant={variant} className={cn("uppercase text-xs")}>
      {label}
    </Badge>
  );
}

function LogLevelBadge({ level }: { level: string }) {
  const normalized = level.toLowerCase();
  const variant =
    normalized === "error" || normalized === "critical"
      ? "destructive"
      : normalized === "warning" || normalized === "warn"
        ? "secondary"
        : "outline";

  return (
    <Badge variant={variant} className="uppercase text-xs">
      {level}
    </Badge>
  );
}

function formatDate(iso?: string | null) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

function formatRelativeTime(iso: string) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const now = new Date();
    const s = Math.round((now.getTime() - d.getTime()) / 1000);
    if (s < 60) return "Just now";
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
}

function formatDuration(seconds?: number | null) {
  if (seconds === undefined || seconds === null) return "—";
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return remainingSeconds > 0
      ? `${minutes}m ${remainingSeconds}s`
      : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
}

function formatLogDetails(details?: Record<string, unknown> | null) {
  if (!details || Object.keys(details).length === 0) return null;
  return JSON.stringify(details, null, 2);
}

function renderJsonWithLinks(jsonStr: string): string {
  const escaped = jsonStr
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return escaped.replace(/\bhttps?:\/\/[^\s"'<]+/g, (url) => {
    const href = url.replace(/"/g, "&quot;");
    return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="underline text-primary hover:text-primary/80">${url}</a>`;
  });
}

function extractUrl(details?: Record<string, unknown> | null): string | null {
  if (!details) return null;
  if (typeof details.url === "string" && /^https?:\/\//i.test(details.url)) {
    return details.url;
  }
  for (const value of Object.values(details)) {
    if (typeof value === "string" && /^https?:\/\//i.test(value)) {
      return value;
    }
  }
  return null;
}

type ScrapeEntryState = {
  status: "idle" | "loading" | "success" | "error";
  jobId?: string;
  error?: string;
};

function RunLogsModal({
  open,
  onOpenChange,
  run,
  runId,
  logs,
  isLoading,
  error,
  onViewResult,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  run?: ScraperRun | undefined;
  runId?: number | undefined;
  logs: ScraperLog[];
  isLoading: boolean;
  error?: Error | null;
  onViewResult?: (jobId: string) => void;
}) {
  const titleRunId = run?.id ?? runId;
  const [scrapeStates, setScrapeStates] = useState<
    Record<number, ScrapeEntryState>
  >({});

  const handleRescrape = async (logId: number, url: string) => {
    setScrapeStates((prev) => ({ ...prev, [logId]: { status: "loading" } }));
    try {
      const result = await scrapeUrl(url);
      setScrapeStates((prev) => ({
        ...prev,
        [logId]: {
          status: "success",
          ...(result.job_id ? { jobId: result.job_id } : {}),
        },
      }));
    } catch (err) {
      setScrapeStates((prev) => ({
        ...prev,
        [logId]: {
          status: "error",
          error: err instanceof Error ? err.message : "Unknown error",
        },
      }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-4xl overflow-hidden flex flex-col">
        <DialogHeader className="flex-none">
          <DialogTitle>
            Run logs{titleRunId ? ` #${titleRunId}` : ""}
          </DialogTitle>
          <DialogDescription>
            {run
              ? `${run.source} ${run.status} run started ${formatDate(run.started_at)}`
              : "Logs for the selected scraper run."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto pr-2">
          {isLoading ? (
            <div className="flex items-center gap-2 py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading logs…
            </div>
          ) : error ? (
            <p className="py-8 text-sm text-destructive">{error.message}</p>
          ) : logs.length === 0 ? (
            <p className="py-8 text-sm text-muted-foreground">
              No logs found for this run.
            </p>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => {
                const details = formatLogDetails(log.details);
                const url =
                  log.level === "error" || log.level === "critical"
                    ? extractUrl(log.details)
                    : null;
                const scrapeState = scrapeStates[log.id] ?? {
                  status: "idle",
                };

                return (
                  <div
                    key={log.id}
                    className="rounded-lg border bg-background p-3"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <LogLevelBadge level={log.level} />
                      <span className="font-mono text-xs text-muted-foreground">
                        {formatDate(log.created_at)}
                      </span>
                    </div>
                    <p className="mt-2 whitespace-pre-wrap text-sm">
                      {log.message}
                    </p>
                    {url !== null && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {scrapeState.status === "idle" && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5"
                            onClick={() => handleRescrape(log.id, url)}
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                            Rescrape
                          </Button>
                        )}
                        {scrapeState.status === "loading" && (
                          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Scraping…
                          </div>
                        )}
                        {scrapeState.status === "success" && (
                          <>
                            <Badge className="bg-green-500/20 text-green-700 dark:text-green-400">
                              Scraped
                            </Badge>
                            {scrapeState.jobId && onViewResult && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="gap-1.5"
                                onClick={() => onViewResult(scrapeState.jobId!)}
                              >
                                <FileJson className="h-3.5 w-3.5" />
                                View result
                              </Button>
                            )}
                          </>
                        )}
                        {scrapeState.status === "error" && (
                          <p className="text-xs text-destructive">
                            {scrapeState.error}
                          </p>
                        )}
                      </div>
                    )}
                    {details ? (
                      <JsonBlock
                        value={details}
                        html={renderJsonWithLinks(details)}
                        className="mt-3 max-h-72 overflow-auto rounded-md bg-muted p-3 text-xs"
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Aggregate URL summaries by source: last scraped = max(last_scraped_at), count = URLs. */
function useSourceScrapeStats() {
  const { data: summaries = [], isLoading } = useUrlsSummary({
    limit: 1000,
    offset: 0,
  });
  const bySource = summaries.reduce<
    Record<string, { lastScrapedAt: string; urlCount: number }>
  >((acc, s) => {
    const source = s.source ?? "unknown";
    if (!acc[source]) acc[source] = { lastScrapedAt: "", urlCount: 0 };
    acc[source].urlCount += 1;
    const at = s.last_scraped_at ?? "";
    if (at && (!acc[source].lastScrapedAt || at > acc[source].lastScrapedAt)) {
      acc[source].lastScrapedAt = at;
    }
    return acc;
  }, {});
  const sourceStats = Object.entries(bySource)
    .map(([source, { lastScrapedAt, urlCount }]) => ({
      source,
      lastScrapedAt,
      urlCount,
    }))
    .sort((a, b) => a.source.localeCompare(b.source));
  return { sourceStats, isLoading };
}

export default function Scrapes() {
  const [urlState, setUrlState] = useUrlState<ScrapesUrlState>({
    schema: {
      tab: "string",
      status: "string",
      source: "string",
      page: "number",
      sortKey: "string",
      sortDirection: "string",
    },
    defaults: {
      tab: "start",
      page: 1,
      sortKey: DEFAULT_SORT_KEY,
      sortDirection: DEFAULT_SORT_DIRECTION,
    },
    replace: true,
  });

  const [sessionState, setSessionState] = useSessionState<ScrapesSessionState>({
    key: "scrapes-ui",
    defaultValue: {
      urlScrapeDialogOpen: false,
      sourceScrapeDialogOpen: false,
      resultModalOpen: false,
      runLogsModalOpen: false,
    },
  });

  const {
    data: jobsData,
    isLoading: jobsLoading,
    refetch: refetchJobs,
  } = useJobList(5000);
  const deleteJobsMutation = useDeleteJobs();
  const { sourceStats, isLoading: sourceStatsLoading } = useSourceScrapeStats();
  const { data: scraperSources = [], isLoading: scraperSourcesLoading } =
    useScrapersSources();
  const {
    data: scraperRuns = [],
    isLoading: scraperRunsLoading,
    refetch: refetchScraperRuns,
  } = useScraperRuns(
    urlState.source ? { source: urlState.source, limit: 50 } : { limit: 50 },
    5000,
  );
  const scraperRunLogsMutation = useScraperRunLogs();

  const allJobs = jobsData?.jobs ?? [];
  const scrapeJobs = allJobs.filter((j) =>
    SCRAPE_FUNCTION_PATTERN.test(String(j.function ?? "")),
  );
  const statusFilter = urlState.status;
  const filteredJobs = scrapeJobs.filter((j) => {
    if (statusFilter && j.status !== statusFilter) return false;
    return true;
  });
  const activeScrapeJobs = scrapeJobs.filter(
    (j) => j.status === "queued" || j.status === "in_progress",
  );
  const queuedScrapeCount = scrapeJobs.filter(
    (j) => j.status === "queued",
  ).length;
  const runningRunCount = scraperRuns.filter(
    (run) => run.status === "running",
  ).length;
  const sourceOptions = Array.from(
    new Set([...scraperSources, ...scraperRuns.map((run) => run.source)]),
  ).sort((a, b) => a.localeCompare(b));
  const selectedRun = scraperRuns.find(
    (run) => run.id === sessionState.runLogsRunId,
  );

  const currentSortKey: SortKey =
    (urlState.sortKey as SortKey | undefined) ?? DEFAULT_SORT_KEY;
  const currentSortDirection: SortDirection =
    (urlState.sortDirection as SortDirection | undefined) ??
    DEFAULT_SORT_DIRECTION;

  const sortJobs = (list: JobListItem[]): JobListItem[] => {
    const sorted = [...list].sort((a, b) => {
      const key = currentSortKey;
      let aVal: string | number | null | undefined;
      let bVal: string | number | null | undefined;

      if (key === "job_id") {
        aVal = a.job_id;
        bVal = b.job_id;
      } else if (key === "function") {
        aVal = a.function ?? "";
        bVal = b.function ?? "";
      } else if (key === "status") {
        aVal = a.status;
        bVal = b.status;
      } else {
        const aTime = a.enqueue_time ?? a.created_at ?? null;
        const bTime = b.enqueue_time ?? b.created_at ?? null;
        const aDate = aTime ? new Date(aTime).getTime() : 0;
        const bDate = bTime ? new Date(bTime).getTime() : 0;
        aVal = aDate;
        bVal = bDate;
      }

      if (aVal === bVal) return 0;
      if (aVal === undefined || aVal === null) return -1;
      if (bVal === undefined || bVal === null) return 1;

      if (typeof aVal === "number" && typeof bVal === "number") {
        return aVal - bVal;
      }

      return String(aVal).localeCompare(String(bVal));
    });

    return currentSortDirection === "asc" ? sorted : sorted.reverse();
  };

  const sortedActiveScrapeJobs = sortJobs(activeScrapeJobs);
  const sortedFilteredJobs = sortJobs(filteredJobs);

  const handleSortChange = (key: SortKey) => {
    setUrlState((prev) => {
      const nextDirection: SortDirection =
        (prev.sortKey as SortKey | undefined) === key &&
        prev.sortDirection === "asc"
          ? "desc"
          : "asc";

      return {
        ...prev,
        sortKey: key,
        sortDirection: nextDirection,
      };
    });
  };

  const handleResultClick = (job: JobListItem) => {
    setSessionState((prev) => ({
      ...prev,
      resultJobId: job.job_id,
      resultModalOpen: true,
    }));
  };

  const handleRunsSourceChange = (source: string) => {
    setUrlState((prev) => {
      if (!source) {
        const { source: _removed, ...rest } = prev;
        return { ...rest, page: 1 };
      }
      return { ...prev, source, page: 1 };
    });
  };

  const handleRunLogsClick = (run: ScraperRun) => {
    setSessionState((prev) => ({
      ...prev,
      runLogsRunId: run.id,
      runLogsModalOpen: true,
    }));
    scraperRunLogsMutation.reset();
    scraperRunLogsMutation.mutate(
      { runId: run.id, limit: 200 },
      {
        onError: (error) => {
          toast.error(
            error instanceof Error ? error.message : "Failed to load run logs",
          );
        },
      },
    );
  };

  const handleDeleteQueued = async () => {
    if (queuedScrapeCount === 0) {
      toast.info("No queued scrape jobs to delete");
      return;
    }
    if (
      !window.confirm(
        `Delete all ${queuedScrapeCount} queued scrape job(s)? In-progress jobs will not be stopped.`,
      )
    ) {
      return;
    }
    try {
      await deleteJobsMutation.mutateAsync("queued");
      toast.success("Queued jobs deleted");
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to delete queued jobs",
      );
    }
  };

  const tab = urlState.tab ?? "start";

  return (
    <div className="space-y-6">
      <Toaster position="top-right" theme="dark" />

      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="text-xl font-semibold">Scrapes</h2>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              refetchJobs();
              refetchScraperRuns();
            }}
            disabled={jobsLoading || scraperRunsLoading}
          >
            {jobsLoading || scraperRunsLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      <Tabs
        value={tab}
        onValueChange={(value) =>
          setUrlState((prev) => ({ ...prev, tab: value, page: 1 }))
        }
      >
        <TabsList>
          <TabsTrigger value="start">Start scrape</TabsTrigger>
          <TabsTrigger value="jobs">
            Scrape jobs
            {activeScrapeJobs.length > 0 && (
              <Badge variant="secondary" className="ml-1.5">
                {activeScrapeJobs.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="runs">
            Runs
            {runningRunCount > 0 && (
              <Badge variant="secondary" className="ml-1.5">
                {runningRunCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="sources">Source stats</TabsTrigger>
        </TabsList>

        <TabsContent value="start" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Play className="w-5 h-5" />
                Kick off a scrape
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Start a full source scrape or scrape a single URL. Jobs run in
                the background; check the Scrape jobs tab for progress and
                results.
              </p>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                onClick={() =>
                  setSessionState((prev) => ({
                    ...prev,
                    urlScrapeDialogOpen: true,
                  }))
                }
              >
                <Globe className="w-4 h-4 mr-2" />
                Scrape URL
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  setSessionState((prev) => ({
                    ...prev,
                    sourceScrapeDialogOpen: true,
                  }))
                }
              >
                <Database className="w-4 h-4 mr-2" />
                Scrape source
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="jobs" className="space-y-4">
          {activeScrapeJobs.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5" />
                  Active scrape jobs ({activeScrapeJobs.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        <button
                          type="button"
                          onClick={() => handleSortChange("job_id")}
                          className="flex items-center gap-1 text-xs font-medium text-left"
                        >
                          <span>Job ID</span>
                          {renderSortIcon(
                            "job_id",
                            currentSortKey,
                            currentSortDirection,
                          )}
                        </button>
                      </TableHead>
                      <TableHead>
                        <button
                          type="button"
                          onClick={() => handleSortChange("function")}
                          className="flex items-center gap-1 text-xs font-medium text-left"
                        >
                          <span>Function</span>
                          {renderSortIcon(
                            "function",
                            currentSortKey,
                            currentSortDirection,
                          )}
                        </button>
                      </TableHead>
                      <TableHead>
                        <button
                          type="button"
                          onClick={() => handleSortChange("status")}
                          className="flex items-center gap-1 text-xs font-medium text-left"
                        >
                          <span>Status</span>
                          {renderSortIcon(
                            "status",
                            currentSortKey,
                            currentSortDirection,
                          )}
                        </button>
                      </TableHead>
                      <TableHead className="text-right">
                        <button
                          type="button"
                          onClick={() => handleSortChange("enqueue_time")}
                          className="flex items-center gap-1 text-xs font-medium w-full justify-end"
                        >
                          <span>Enqueue time</span>
                          {renderSortIcon(
                            "enqueue_time",
                            currentSortKey,
                            currentSortDirection,
                          )}
                        </button>
                      </TableHead>
                      <TableHead className="w-[100px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedActiveScrapeJobs.map((job) => (
                      <TableRow key={job.job_id}>
                        <TableCell className="font-mono text-xs">
                          {job.job_id}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {job.function ?? "—"}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={job.status} />
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground text-xs">
                          {formatDate(job.enqueue_time ?? job.created_at)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResultClick(job)}
                            className="gap-1.5"
                          >
                            <FileJson className="w-3.5 h-3.5" />
                            Result
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                All scrape jobs
                <span className="text-muted-foreground font-normal text-sm">
                  ({filteredJobs.length} shown)
                </span>
              </CardTitle>
              <div className="flex flex-wrap gap-2 pt-2">
                <select
                  className="rounded-md border bg-background px-2 py-1 text-sm"
                  value={statusFilter ?? ""}
                  onChange={(e) =>
                    setUrlState((prev) => {
                      if (!e.target.value) {
                        const { status: _removed, ...rest } = prev;
                        return { ...rest, page: 1 };
                      }
                      return {
                        ...prev,
                        status: e.target.value,
                        page: 1,
                      };
                    })
                  }
                >
                  <option value="">All statuses</option>
                  <option value="queued">Queued</option>
                  <option value="in_progress">In progress</option>
                  <option value="complete">Complete</option>
                  <option value="failed">Failed</option>
                </select>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteQueued}
                  disabled={
                    queuedScrapeCount === 0 || deleteJobsMutation.isPending
                  }
                >
                  {deleteJobsMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : null}
                  Delete queued ({queuedScrapeCount})
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {jobsLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-8">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Loading jobs…
                </div>
              ) : filteredJobs.length === 0 ? (
                <p className="text-muted-foreground py-8">
                  {scrapeJobs.length === 0
                    ? "No scrape jobs found. Start a scrape from the Start scrape tab."
                    : "No scrape jobs match the current filters."}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>
                        <button
                          type="button"
                          onClick={() => handleSortChange("job_id")}
                          className="flex items-center gap-1 text-xs font-medium text-left"
                        >
                          <span>Job ID</span>
                          {renderSortIcon(
                            "job_id",
                            currentSortKey,
                            currentSortDirection,
                          )}
                        </button>
                      </TableHead>
                      <TableHead>
                        <button
                          type="button"
                          onClick={() => handleSortChange("function")}
                          className="flex items-center gap-1 text-xs font-medium text-left"
                        >
                          <span>Function</span>
                          {renderSortIcon(
                            "function",
                            currentSortKey,
                            currentSortDirection,
                          )}
                        </button>
                      </TableHead>
                      <TableHead>
                        <button
                          type="button"
                          onClick={() => handleSortChange("status")}
                          className="flex items-center gap-1 text-xs font-medium text-left"
                        >
                          <span>Status</span>
                          {renderSortIcon(
                            "status",
                            currentSortKey,
                            currentSortDirection,
                          )}
                        </button>
                      </TableHead>
                      <TableHead className="text-right">
                        <button
                          type="button"
                          onClick={() => handleSortChange("enqueue_time")}
                          className="flex items-center gap-1 text-xs font-medium w-full justify-end"
                        >
                          <span>Enqueue time</span>
                          {renderSortIcon(
                            "enqueue_time",
                            currentSortKey,
                            currentSortDirection,
                          )}
                        </button>
                      </TableHead>
                      <TableHead className="w-[100px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedFilteredJobs.map((job) => (
                      <TableRow key={job.job_id}>
                        <TableCell className="font-mono text-xs">
                          {job.job_id}
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {job.function ?? "—"}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={job.status} />
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground text-xs">
                          {formatDate(job.enqueue_time ?? job.created_at)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResultClick(job)}
                            className="gap-1.5"
                          >
                            <FileJson className="w-3.5 h-3.5" />
                            Result
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="runs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Latest scraper runs
                <span className="text-muted-foreground font-normal text-sm">
                  ({scraperRuns.length} shown)
                </span>
              </CardTitle>
              <div className="flex flex-wrap gap-2 pt-2">
                <select
                  className="rounded-md border bg-background px-2 py-1 text-sm"
                  value={urlState.source ?? ""}
                  onChange={(e) => handleRunsSourceChange(e.target.value)}
                  disabled={scraperSourcesLoading}
                >
                  <option value="">All sources</option>
                  {sourceOptions.map((source) => (
                    <option key={source} value={source}>
                      {source}
                    </option>
                  ))}
                </select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetchScraperRuns()}
                  disabled={scraperRunsLoading}
                >
                  {scraperRunsLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  Refresh runs
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {scraperRunsLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-8">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Loading scraper runs…
                </div>
              ) : scraperRuns.length === 0 ? (
                <p className="text-muted-foreground py-8">
                  {urlState.source
                    ? `No scraper runs found for ${urlState.source}.`
                    : "No scraper runs found."}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Run ID</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Completed</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead className="text-right">Products</TableHead>
                      <TableHead>Error</TableHead>
                      <TableHead className="w-[100px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scraperRuns.map((run) => (
                      <TableRow key={run.id}>
                        <TableCell className="font-mono text-xs">
                          {run.id}
                        </TableCell>
                        <TableCell className="font-medium">
                          {run.source}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={run.status} />
                        </TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {formatDate(run.started_at)}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {formatDate(run.completed_at)}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {formatDuration(run.duration_seconds)}
                        </TableCell>
                        <TableCell className="text-right text-xs">
                          <span title="found / updated / new">
                            {run.products_found ?? "—"} /{" "}
                            {run.products_updated ?? "—"} /{" "}
                            {run.products_new ?? "—"}
                          </span>
                        </TableCell>
                        <TableCell className="max-w-[240px] truncate text-xs text-muted-foreground">
                          {run.error_message ?? "—"}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRunLogsClick(run)}
                            className="gap-1.5"
                          >
                            <FileText className="w-3.5 h-3.5" />
                            Logs
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sources" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5" />
                Last scraped by source
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Derived from validation URL data. Shows the most recent scrape
                time per source and how many URLs we have for that source.
              </p>
            </CardHeader>
            <CardContent>
              {sourceStatsLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-8">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Loading source stats…
                </div>
              ) : sourceStats.length === 0 ? (
                <p className="text-muted-foreground py-8">
                  No source data yet. Run a scrape and check validation data.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Source</TableHead>
                      <TableHead>Last scraped</TableHead>
                      <TableHead className="text-right">URLs</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sourceStats.map(({ source, lastScrapedAt, urlCount }) => (
                      <TableRow key={source}>
                        <TableCell className="font-medium">{source}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {lastScrapedAt
                            ? formatRelativeTime(lastScrapedAt)
                            : "—"}
                          {lastScrapedAt && (
                            <span className="ml-1 text-xs">
                              ({formatDate(lastScrapedAt)})
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{urlCount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <UrlScrapeDialog
        open={sessionState.urlScrapeDialogOpen}
        onOpenChange={(open) =>
          setSessionState((prev) => ({ ...prev, urlScrapeDialogOpen: open }))
        }
        onViewRunResults={(jobId) =>
          setSessionState((prev) => ({
            ...prev,
            resultJobId: jobId,
            resultModalOpen: true,
            urlScrapeDialogOpen: false,
          }))
        }
      />
      <SourceScrapeDialog
        open={sessionState.sourceScrapeDialogOpen}
        onOpenChange={(open) =>
          setSessionState((prev) => ({ ...prev, sourceScrapeDialogOpen: open }))
        }
        onViewRunResults={(runId) =>
          setSessionState((prev) => ({
            ...prev,
            resultJobId: runId,
            resultModalOpen: true,
            sourceScrapeDialogOpen: false,
          }))
        }
      />

      <JobResultModal
        open={sessionState.resultModalOpen}
        onOpenChange={(open) =>
          setSessionState((prev) => ({ ...prev, resultModalOpen: open }))
        }
        jobId={sessionState.resultJobId}
      />
      <RunLogsModal
        open={sessionState.runLogsModalOpen}
        onOpenChange={(open) =>
          setSessionState((prev) => ({ ...prev, runLogsModalOpen: open }))
        }
        run={selectedRun}
        runId={sessionState.runLogsRunId}
        logs={scraperRunLogsMutation.data ?? []}
        isLoading={scraperRunLogsMutation.isPending}
        error={
          scraperRunLogsMutation.error instanceof Error
            ? scraperRunLogsMutation.error
            : null
        }
        onViewResult={(jobId) =>
          setSessionState((prev) => ({
            ...prev,
            resultJobId: jobId,
            resultModalOpen: true,
            runLogsModalOpen: false,
          }))
        }
      />
    </div>
  );
}
